import { cn } from '@repo/ui';
import type { ReactElement, ReactNode } from 'react';

/**
 * AuthCardLayout — the canvas for every unauthenticated page.
 *
 * Mobile-first: full-width with side padding on `<sm`. From `sm` upward
 * we center a `max-w-md` card with a shadow. From `lg` the card grows
 * slightly to `md` (448px) and acquires the elevated "loginscreen" feel
 * — slimmer padding, deeper shadow.
 *
 * `logo` is intentionally a slot: the ERP shell renders the brand mark
 * here without hard-coding the mark shape (Slice 1 will inject the
 * tenant-scoped logo).
 */
export interface AuthCardLayoutProps {
  title: string;
  subtitle?: string;
  logo?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function AuthCardLayout({
  title,
  subtitle,
  logo,
  children,
  footer,
  className,
}: AuthCardLayoutProps): ReactElement {
  return (
    <main
      className={cn(
        'min-h-screen w-full bg-background text-foreground',
        'grid place-items-center px-4 py-6 sm:py-10',
        className,
      )}
    >
      <div className="w-full sm:max-w-md lg:max-w-md">
        <header className="mb-6 flex flex-col items-center gap-3 sm:mb-8">
          <div
            aria-hidden="true"
            className="grid size-12 place-items-center rounded-xl bg-primary text-primary-foreground shadow-sm"
          >
            {logo ?? <LogoMark />}
          </div>
          <div className="space-y-1 text-center">
            <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
            {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
          </div>
        </header>

        <section className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm sm:p-8">
          {children}
        </section>

        {footer ? (
          <footer className="mt-6 text-center text-sm text-muted-foreground">{footer}</footer>
        ) : null}
      </div>
    </main>
  );
}

/**
 * Inline brand mark — three horizontal strokes (a stylized "Σ"). Lives
 * inline so the layout works in a brand-less white-label context
 * (when the TenantSwitcher wires up a real logo later, this is
 * replaced via the `logo` prop).
 */
function LogoMark(): ReactElement {
  return (
    <svg
      viewBox="0 0 32 32"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M9 9h14" />
      <path d="M9 16h14" />
      <path d="M9 23h9" />
    </svg>
  );
}
